import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { IS_DEMO_MODE } from '@/config/config'
import { PageHeader } from '@/components/ui/PageHeader'
import { Search, Users, AlertTriangle, LayoutList } from 'lucide-react'
import { displayBeneficiaryName } from '@/utils/beneficiaryDisplay'
import { useAppData } from '@/contexts/AppDataContext'
import { displayTag, tagActiveClass, computeNewTags, TagIcon } from '@/components/ui/TagBadges'

// Fields that must be filled - if any is missing a warning is shown
const REQUIRED_TEXT_KEYS = [
    'nom', 'prenom', 'date_naissance', 'registre_national',
    'statut_accompagnement', 'referente',
    'date_premiere_convention', 'date_derniere_convention',
    'organisme_paiement', 'lieu_vie_type',
]
const REQUIRED_BOOL_KEYS = [
    'critere_sans_abri_ok', 'critere_de_longue_duree_ok', 'critere_fragile_ok',
    'critere_accompagnement_intensif_ok', 'critere_source_revenus_ok',
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isIncomplete = (b: Record<string, any>): boolean => {
    for (const k of REQUIRED_TEXT_KEYS) {
        if (!b[k]) return true
    }
    for (const k of REQUIRED_BOOL_KEYS) {
        if (b[k] == null) return true
    }
    return false
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BeneficiaireSummary = Record<string, any>

interface BeneficiairesListProps {
    onNavigateHome: () => void
    onSelectBeneficiaire: (id: string) => void
    onOverview?: () => void
}

const statusBadgeClass = (status: string | null) => {
    if (status === 'Actif') return 'bg-green-100 text-green-800'
    if (status === 'Suspendu') return 'bg-yellow-100 text-yellow-800'
    if (status === 'Terminé') return 'bg-red-100 text-red-700'
    return 'bg-gray-100 text-gray-500'
}

export const BeneficiairesList = ({ onNavigateHome, onSelectBeneficiaire, onOverview }: BeneficiairesListProps) => {
    const { workers, updateSummaryTags } = useAppData()
    const [beneficiaires, setBeneficiaires] = useState<BeneficiaireSummary[]>([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [showInactive, setShowInactive] = useState(false)

    useEffect(() => {
        const load = async () => {
            const { data } = await supabase.from('beneficiaries').select([
                    'id', 'nom', 'prenom', 'numero_suivi',
                    'statut_accompagnement', 'referente', 'situation_residentielle', 'is_active', 'tags',
                    // required fields for completeness check
                    'date_naissance', 'registre_national',
                    'date_premiere_convention', 'date_derniere_convention',
                    'organisme_paiement', 'lieu_vie_type',
                    'critere_sans_abri_ok', 'critere_de_longue_duree_ok', 'critere_fragile_ok',
                    'critere_accompagnement_intensif_ok', 'critere_source_revenus_ok',
                ].join(', '))
                .order('prenom', { ascending: true, nullsFirst: false })
            if (data) setBeneficiaires(data as any)
            setLoading(false)
        }
        load()
    }, [])

    const handleToggleTag = async (id: string, tag: string) => {
        if (IS_DEMO_MODE) return
        const current = beneficiaires.find(b => b.id === id)?.tags ?? []
        const newTags = computeNewTags(current, tag)
        setBeneficiaires(prev => prev.map(b => b.id === id ? { ...b, tags: newTags } : b))
        updateSummaryTags(id, newTags)
        await supabase.from('beneficiaries').update({ tags: newTags }).eq('id', id)
    }

    const filtered = beneficiaires
        .filter(b => showInactive || b.is_active === true)
        .filter(b => {
            if (!search) return true
            const haystack = [b.prenom, b.nom].filter(Boolean).join(' ').toLowerCase()
            return haystack.includes(search.toLowerCase())
        })

    return (
        <div className="min-h-screen bg-gray-50">
            <PageHeader
                title="Locataires"
                icon={<Users className="h-5 w-5" />}
                onBack={onNavigateHome}
            />
            <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
                {onOverview && (
                    <button
                        type="button"
                        onClick={onOverview}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:border-blue-400 hover:text-blue-600 transition-colors"
                    >
                        <LayoutList className="h-4 w-4" />
                        Vue d'ensemble
                    </button>
                )}
                <div className="flex gap-3 items-center">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher par nom, prénom ou alias..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-600 whitespace-nowrap cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={e => setShowInactive(e.target.checked)}
                            className="rounded"
                        />
                        Afficher inactifs
                    </label>
                </div>

                {loading ? (
                    <div className="flex justify-center pt-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                    </div>
                ) : filtered.length === 0 ? (
                    <p className="text-center text-gray-500 py-12">Aucun locataire trouvé</p>
                ) : (
                    <div className="space-y-2">
                        <p className="text-xs text-gray-400 pl-1">
                            {filtered.length} locataire{filtered.length > 1 ? 's' : ''}
                        </p>
                        {filtered.map(b => {
                            const incomplete = isIncomplete(b)
                            const label = displayBeneficiaryName(b)
                            const tags: string[] = b.tags ?? []
                            return (
                                <div
                                    key={b.id}
                                    className={`w-full bg-white rounded-lg border px-4 py-3 hover:shadow-sm transition-all
                                        ${incomplete
                                            ? 'border-red-200 hover:border-red-400'
                                            : 'border-gray-200 hover:border-blue-400'
                                        }`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="min-w-0 flex-1 cursor-pointer text-left" onClick={() => onSelectBeneficiaire(b.id)}>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {incomplete && (
                                                    <AlertTriangle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                                                )}
                                                <p className="font-semibold text-gray-900">{label}</p>
                                            </div>
                                            <p className="text-sm text-gray-500 truncate mt-0.5">
                                                {[workers.find(w => w.id === b.referente)?.name, b.lieu_vie_type ?? b.situation_residentielle].filter(Boolean).join(' · ') || '—'}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {tags.map(tag => (
                                                <button key={tag} type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleToggleTag(b.id, tag) }}
                                                    className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${tagActiveClass(tag)}`}>
                                                    <TagIcon tag={tag} size={11} className="sm:hidden" />
                                                    <span className="hidden sm:inline">{displayTag(tag)}</span>
                                                </button>
                                            ))}
                                            {!IS_DEMO_MODE && !tags.includes('binome') && (
                                                <button type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleToggleTag(b.id, 'binome') }}
                                                    className="px-2 py-0.5 rounded-full text-xs border bg-white text-gray-400 border-gray-200 hover:bg-gray-50 flex items-center gap-0.5">
                                                    <TagIcon tag="binome" size={11} className="sm:hidden" />
                                                    <span className="hidden sm:inline">+ binôme</span>
                                                </button>
                                            )}
                                            {!IS_DEMO_MODE && !tags.includes('solo') && (
                                                <button type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleToggleTag(b.id, 'solo') }}
                                                    className="px-2 py-0.5 rounded-full text-xs border bg-white text-gray-400 border-gray-200 hover:bg-gray-50 flex items-center gap-0.5">
                                                    <TagIcon tag="solo" size={11} className="sm:hidden" />
                                                    <span className="hidden sm:inline">+ solo</span>
                                                </button>
                                            )}
                                            {b.statut_accompagnement && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadgeClass(b.statut_accompagnement)}`}>
                                                    {b.statut_accompagnement}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}
